// The calendar: MUI X's DatePicker and DateRangePicker as the legacy views
// used them, drawn with @mui/material.
//
// The legacy application used @mui/x-date-pickers(-pro) 6, which is licensed
// and does not run on React 19. What it drew is rebuilt here to the same
// measures — a 320px calendar, 36px days with a 2px margin, single-letter
// weekdays, the month and year over them — and with the same behaviour: a
// single field opens one month; the pair of range fields opens two months
// side by side on a desktop and one on a phone, with the days between the two
// ends shaded. On a desktop a complete choice closes the picker at once
// (closeOnSelect); on a phone the choice waits for the accept button.

import { useEffect, useRef, useState } from "react";
import { alpha, Box, ButtonBase, IconButton, Stack, TextField, Typography, useTheme } from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CalendarMonthIcon from "@mui/icons-material/Event";
import { useLang, useT } from "~/i18n";
import { fmtDate, sameDay, startOfDay, viennaNow } from "./dates";
import { useBelowSm } from "./media";
import { ActionBar, Floating, Modal, PickerField } from "./popup";

type Range = [Date | null, Date | null];

const locales = { de: "de-AT", en: "en-US" } as const;

const DAY = 36;
const MARGIN = 2;

function firstOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function addMonths(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}

function ArrowLeft() {
  return (
    <svg width="1em" height="1em" viewBox="0 0 24 24" fill="currentColor" style={{ fontSize: "1.5rem" }} aria-hidden>
      <path d="M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6 1.41-1.41z" />
    </svg>
  );
}

function ArrowRight() {
  return (
    <svg width="1em" height="1em" viewBox="0 0 24 24" fill="currentColor" style={{ fontSize: "1.5rem" }} aria-hidden>
      <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" />
    </svg>
  );
}

function useMonthTitle() {
  const lang = useLang();
  return (month: Date) => {
    const s = new Intl.DateTimeFormat(locales[lang], { month: "long", year: "numeric" }).format(month);
    return s.charAt(0).toUpperCase() + s.slice(1);
  };
}

// --- the grid of days ----------------------------------------------------------------

function Days({
  month,
  range,
  onPick,
  rowMargin = MARGIN,
}: {
  month: Date;
  range: Range;
  onPick: (day: Date) => void;
  /** The range calendar spaces its weeks 3px apart, the single one 2px. */
  rowMargin?: number;
}) {
  const theme = useTheme();
  const lang = useLang();
  const today = startOfDay(viennaNow());
  // moment's German week starts on Monday and its English one on Sunday, and
  // the legacy calendar followed the language.
  const weekStart = lang === "de" ? 1 : 0;

  const weekdays = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(2024, 0, 7 + ((weekStart + i) % 7)); // 2024-01-07 is a Sunday
    return new Intl.DateTimeFormat(locales[lang], { weekday: "short" }).format(d).charAt(0).toUpperCase();
  });

  const lead = (month.getDay() - weekStart + 7) % 7;
  const count = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const cells: (Date | null)[] = [
    ...Array.from({ length: lead }, () => null),
    ...Array.from({ length: count }, (_, i) => new Date(month.getFullYear(), month.getMonth(), i + 1)),
  ];
  while (cells.length % 7 !== 0) cells.push(null);
  const weeks = Array.from({ length: cells.length / 7 }, (_, i) => cells.slice(i * 7, i * 7 + 7));

  const [from, to] = range;
  const isRange = !!from && !!to;
  const highlight = alpha(theme.palette.primary.main, theme.palette.action.focusOpacity);

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
        {weekdays.map((w, i) => (
          <Typography
            key={i}
            variant="caption"
            sx={{
              width: DAY,
              height: 40,
              m: `0 ${MARGIN}px`,
              textAlign: "center",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              color: "text.secondary",
            }}
          >
            {w}
          </Typography>
        ))}
      </Box>
      <Box sx={{ minHeight: (DAY + rowMargin * 2) * 6 }}>
        {weeks.map((week, wi) => (
          <Box key={wi} sx={{ m: `${rowMargin}px 0`, display: "flex", justifyContent: "center" }}>
            {week.map((d, i) => {
              if (!d) return <Box key={i} sx={{ width: DAY + MARGIN * 2, height: DAY }} />;
              const isFrom = !!from && sameDay(d, from);
              const isTo = !!to && sameDay(d, to);
              const selected = isFrom || isTo;
              const inside = isRange && !!from && !!to && d > from && d < to;
              const band = isRange && !!from && !!to && !sameDay(from, to) && (inside || isFrom || isTo);
              const first = i === 0 || d.getDate() === 1;
              const last = i === 6 || d.getDate() === count;
              const roundLeft = isFrom || first;
              const roundRight = isTo || last;
              return (
                <Box
                  key={i}
                  sx={{
                    px: `${MARGIN}px`,
                    bgcolor: band ? highlight : "transparent",
                    borderTopLeftRadius: band && roundLeft ? "50%" : 0,
                    borderBottomLeftRadius: band && roundLeft ? "50%" : 0,
                    borderTopRightRadius: band && roundRight ? "50%" : 0,
                    borderBottomRightRadius: band && roundRight ? "50%" : 0,
                  }}
                >
                  <ButtonBase
                    onClick={() => onPick(d)}
                    sx={{
                      ...theme.typography.caption,
                      width: DAY,
                      height: DAY,
                      borderRadius: "50%",
                      p: 0,
                      color: selected ? "primary.contrastText" : "text.primary",
                      bgcolor: selected ? "primary.main" : "transparent",
                      fontWeight: selected ? theme.typography.fontWeightMedium : undefined,
                      border: !selected && sameDay(d, today) ? `1px solid ${theme.palette.text.secondary}` : "none",
                      "&:hover": {
                        bgcolor: selected
                          ? "primary.dark"
                          : alpha(theme.palette.primary.main, theme.palette.action.hoverOpacity),
                      },
                    }}
                  >
                    {d.getDate()}
                  </ButtonBase>
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

// --- one calendar, as DateCalendar draws it -------------------------------------------

function Years({ value, onPick }: { value: Date; onPick: (year: number) => void }) {
  const list = useRef<HTMLDivElement>(null);
  const year = value.getFullYear();
  useEffect(() => {
    const el = list.current?.querySelector<HTMLElement>(`[data-year="${year}"]`);
    if (el && list.current) list.current.scrollTop = el.offsetTop - list.current.clientHeight / 2;
  }, [year]);
  return (
    <Box
      ref={list}
      sx={{ display: "flex", flexWrap: "wrap", overflowY: "auto", height: "100%", maxHeight: 280, px: "4px" }}
    >
      {Array.from({ length: 200 }, (_, i) => 1900 + i).map((y) => (
        <Box key={y} data-year={y} sx={{ flexBasis: "33.3%", display: "flex", justifyContent: "center", alignItems: "center" }}>
          <ButtonBase
            onClick={() => onPick(y)}
            sx={(theme) => ({
              ...theme.typography.subtitle1,
              my: "4px",
              height: 36,
              width: 72,
              borderRadius: "18px",
              color: y === year ? "primary.contrastText" : "text.primary",
              bgcolor: y === year ? "primary.main" : "transparent",
              "&:hover": { bgcolor: y === year ? "primary.dark" : alpha(theme.palette.action.active, theme.palette.action.hoverOpacity) },
            })}
          >
            {y}
          </ButtonBase>
        </Box>
      ))}
    </Box>
  );
}

function SingleCalendar({ value, onPick }: { value: Date; onPick: (day: Date) => void }) {
  const t = useT();
  const title = useMonthTitle();
  const [month, setMonth] = useState(() => firstOfMonth(value));
  const [view, setView] = useState<"day" | "year">("day");
  return (
    <Box sx={{ width: 320, maxHeight: 334, display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <Box sx={{ display: "flex", alignItems: "center", mt: 2, mb: 1, pl: 3, pr: 1.5, maxHeight: 30, minHeight: 30 }}>
        <Box
          role="presentation"
          onClick={() => setView(view === "day" ? "year" : "day")}
          sx={{ display: "flex", alignItems: "center", overflow: "hidden", cursor: "pointer", mr: "auto" }}
        >
          <Typography sx={{ fontWeight: 500, mr: 0.75 }}>{title(month)}</Typography>
          <IconButton size="small" sx={{ mr: "auto" }} aria-label="year">
            <ArrowDropDownIcon sx={{ transform: view === "year" ? "rotate(180deg)" : "none", transition: "transform 200ms" }} />
          </IconButton>
        </Box>
        {view === "day" ? (
          <Box sx={{ display: "flex" }}>
            <IconButton size="medium" onClick={() => setMonth(addMonths(month, -1))} aria-label={t("previous-month")}>
              <ArrowLeft />
            </IconButton>
            <IconButton size="medium" onClick={() => setMonth(addMonths(month, 1))} aria-label={t("next-month")}>
              <ArrowRight />
            </IconButton>
          </Box>
        ) : null}
      </Box>
      {view === "day" ? (
        <Days month={month} range={[startOfDay(value), null]} onPick={onPick} />
      ) : (
        <Years
          value={month}
          onPick={(y) => {
            setMonth(new Date(y, month.getMonth(), 1));
            setView("day");
          }}
        />
      )}
    </Box>
  );
}

/** RangeCalendar is DateRangeCalendar: two months on a desktop, one on a phone. */
function RangeCalendar({ range, onPick, months }: { range: Range; onPick: (day: Date) => void; months: 1 | 2 }) {
  const t = useT();
  const title = useMonthTitle();
  const theme = useTheme();
  const [first, setFirst] = useState(() => firstOfMonth(range[0] ?? viennaNow()));
  const shown = months === 2 ? [first, addMonths(first, 1)] : [first];
  return (
    <Box sx={{ display: "flex", flexDirection: "row" }}>
      {shown.map((m, i) => (
        <Box
          key={i}
          sx={{
            width: 312,
            "&:not(:last-of-type)": { borderRight: `2px solid ${theme.palette.divider}` },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, pt: 2, pb: 1 }}>
            <IconButton
              onClick={() => setFirst(addMonths(first, -1))}
              aria-label={t("previous-month")}
              sx={{ visibility: i === 0 ? "visible" : "hidden" }}
            >
              <ArrowLeft />
            </IconButton>
            <Typography variant="subtitle1">{title(m)}</Typography>
            <IconButton
              onClick={() => setFirst(addMonths(first, 1))}
              aria-label={t("next-month")}
              sx={{ visibility: i === shown.length - 1 ? "visible" : "hidden" }}
            >
              <ArrowRight />
            </IconButton>
          </Box>
          <Days month={m} range={range} onPick={onPick} rowMargin={3} />
        </Box>
      ))}
    </Box>
  );
}

// --- the fields ---------------------------------------------------------------------------

/** DateField is one date: the legacy DatePicker. */
export function DateField({
  label,
  value,
  onChange,
  disabled = false,
}: {
  label: string;
  value: Date;
  onChange: (day: Date) => void;
  disabled?: boolean;
}) {
  const mobile = useBelowSm();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<Date>(value);

  const close = () => setOpen(false);
  const pick = (day: Date) => {
    if (mobile) {
      setDraft(day);
      return;
    }
    onChange(day);
    setOpen(false);
  };

  return (
    <>
      <PickerField
        label={label}
        value={fmtDate(value)}
        focused={open}
        disabled={disabled}
        // The desktop DatePicker carries the calendar button; the mobile one
        // does not.
        icon={mobile ? undefined : <CalendarMonthIcon />}
        onOpen={(a) => {
          setAnchor(a);
          setDraft(startOfDay(value));
          setOpen(!open);
        }}
      />
      {mobile ? (
        <Modal open={open} onDismiss={close}>
          <SingleCalendar key={String(open)} value={draft} onPick={pick} />
          <ActionBar
            onCancel={close}
            onAccept={() => {
              onChange(draft);
              close();
            }}
          />
        </Modal>
      ) : (
        <Floating open={open} anchor={anchor} onClickAway={close}>
          <SingleCalendar key={String(open)} value={draft} onPick={pick} />
        </Floating>
      )}
    </>
  );
}

/**
 * DateRangeField is the legacy DateRangePicker: two fields with a dash between
 * them, one calendar.
 *
 * Whichever field was pressed is the one the next day goes into. A day before
 * the start becomes the new start; a complete pair closes the picker on a
 * desktop. Accepting with only a first day chosen means "one day", which the
 * caller turns into an end the day after.
 */
export function DateRangeField({
  startLabel,
  endLabel,
  value,
  onChange,
}: {
  startLabel: string;
  endLabel: string;
  value: [Date, Date];
  onChange: (days: [Date, Date | null]) => void;
}) {
  const mobile = useBelowSm();
  const row = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<"start" | "end">("start");
  const [draft, setDraft] = useState<Range>([null, null]);

  const openAt = (side: "start" | "end") => {
    if (open && position === side) {
      setOpen(false);
      return;
    }
    setDraft([startOfDay(value[0]), startOfDay(value[1])]);
    setPosition(side);
    setOpen(true);
  };

  const close = () => setOpen(false);
  const commit = (r: Range) => {
    if (!r[0]) return;
    onChange([r[0], r[1]]);
    setOpen(false);
  };

  const pick = (day: Date) => {
    let next: Range;
    if (position === "start" || !draft[0] || day <= draft[0]) {
      next = [day, null];
      setPosition("end");
    } else {
      next = [draft[0], day];
    }
    setDraft(next);
    if (!mobile && next[1]) commit(next);
  };

  const field = (side: "start" | "end") => (
    <TextField
      variant="outlined"
      label={side === "start" ? startLabel : endLabel}
      value={fmtDate(side === "start" ? value[0] : value[1])}
      onClick={() => openAt(side)}
      inputProps={{ readOnly: true }}
      focused={(open && position === side) || undefined}
      InputLabelProps={{ shrink: true }}
      sx={{ flex: 1, minWidth: 0 }}
    />
  );

  return (
    <>
      <Stack ref={row} direction="row" spacing={2} alignItems="baseline">
        {field("start")}
        <Typography>–</Typography>
        {field("end")}
      </Stack>
      {mobile ? (
        <Modal open={open} onDismiss={close}>
          <RangeCalendar key={String(open)} range={draft} onPick={pick} months={1} />
          <ActionBar onCancel={close} onAccept={() => commit(draft)} />
        </Modal>
      ) : (
        <Floating open={open} anchor={row.current} onClickAway={close}>
          <RangeCalendar key={String(open)} range={draft} onPick={pick} months={2} />
        </Floating>
      )}
    </>
  );
}
